<template>
  <v-card
    :height="widgetHeight"
    :min-width="widgetWidth"
    class="ma-0 pa-0"
    @mouseover.native="hover = true"
    @mouseleave.native="hover = false"
  >
    <v-card-title class="pa-0 ma-0">
      <v-col cols="12" sm="3" justify="left" class="pa-0 ma-0">
        <v-icon class="ml-1">mdi-newspaper</v-icon>
      </v-col>
      <v-spacer></v-spacer>
      <v-col cols="12" sm="4" class="pa-0 ma-0 d-flex flex-row-reverse">
        <div style="height: 30px">
          <v-tooltip top v-if="enableEdit && isOrgAdmin">
            <template v-slot:activator="{ on }">
              <v-icon
                v-on="on"
                class="mr-2"
                @click.prevent="showSettings"
                >mdi-cog</v-icon
              >
            </template>
            <span>{{ $t("Settings") }}</span>
          </v-tooltip>
          <v-tooltip top v-if="enableEdit">
            <template v-slot:activator="{ on }">
              <v-icon
                v-on="on"
                class="mr-2"
                @click.prevent="removeWidget"
                >mdi-delete</v-icon
              >
            </template>
            <span>{{ $t("Delete") }}</span>
          </v-tooltip>
        </div>
      </v-col>
    </v-card-title>
    <v-card-text
      class="pa-0 ma-0"
      :style="`height: ${contentHeight}px;max-height: ${contentHeight}px`"
    >
      <div>
        <base-no-data v-if="!loading && !hasData && widgetHeight > 39" />
        <v-carousel
          dense
          v-model="selectedSlide"
          height="100%"
          v-if="!loading && hasData"
          :cycle="cycle"
          :show-arrows="hover"
          hide-delimiter-background
          :style="`height: ${contentHeight - footerHeight}px;max-height: ${
            contentHeight - footerHeight
          }px`"
        >
          <v-carousel-item v-for="data in newsList" :key="data.id">
            <v-sheet
              :color="data.backgroundColor"
              height="100%"
              tile
              :style="{ color: data.textColor }"
            >
              <v-row class="mx-2" align="center" justify="center">
                <div class="newsfeed-html-content" v-html="data.message" />
              </v-row>
            </v-sheet>
          </v-carousel-item>
        </v-carousel>
        <v-list
          dense
          v-if="!loading && hasData"
          :style="`height: ${footerHeight}px;max-height: ${footerHeight}px`"
        >
          <v-list-item>
            <v-list-item-avatar>
              <v-icon>mdi-account</v-icon>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title>{{ author }}</v-list-item-title>
              <v-list-item-subtitle>{{ $t("Author") }}</v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action>
              <v-switch v-model="cycle" inset dense>
                <template v-slot:label>
                  <v-icon>mdi-repeat</v-icon>
                </template>
              </v-switch>
            </v-list-item-action>
          </v-list-item>
        </v-list>
      </div>
      <SettingsModal :ref="modalKey" @on-save="onSettingsSave" />
    </v-card-text>
  </v-card>
</template>

<script>
import PluginService from '@/services/PluginService';
import SettingsModal from '@/components/widgets/newsfeed/WidgetNewsfeedData';
import Model from '@/ecm/models/widget/newsfeed/NewsfeedSettings';
import Utils from '@/ecm/common/Utils';

export default {
    components: { SettingsModal },
    props: {
        widgetId: {
            type: Number,
            default: 0,
        },
        id: {
            type: Number,
            default: 0,
        },
        enableEdit: {
            type: Boolean,
            default: false,
        },
        height: { type: Number, default: 0 },
        width: { type: Number, default: 0 },
        settings: { type: Object },
    },
    mounted() {
        this.widgetHeight = this.height;
        this.widgetWidth = this.width;
        this.refresh();

        this.connection = $.hubConnection(this.$store.getters['account/apiUrl']);

        this.connection.qs = { access_token: this.$store.getters['account/token'] };

        var notificationHub = this.connection.createHubProxy('notificationHub');

        notificationHub.on('refreshNewsfeedData', () => {
            this.refresh();
        });

        var self = this;
        notificationHub.connection.disconnected(function () {
            setTimeout(function () {
                if (self.connection) {
                    self.connection.start();
                }
            }, 5000); // Restart connection after 5 seconds.
        });

        this.connection.start();
    },
    destroyed() {
        if (this.connection) {
            this.connection.stop();
            this.connection = null;
        }
    },
    data() {
        return {
            connection: null,
            selectedSlide: 0,
            cycle: true,
            isOrgAdmin: this.$store.getters['account/user'].SysLevel == 2,
            colors: ['primary', 'secondary', 'yellow darken-2', 'red', 'orange'],
            hover: false,
            headerHeight: 30,
            widgetHeight: 0,
            widgetWidth: 0,
            footerHeight: 60,
            loading: false,
            hasData: false,
            modalKey: 'modalSettingsKey',
            dbTableName: 'widgetNewsfeed',
            dbTableColumns: [
                'userName',
                'backgroundColor',
                'textColor',
                'startDate',
                'startTime',
                'endDate',
                'endTime',
                'target',
                'targetValue',
                'message',
                'description',
            ],
            newsList: [],
        };
    },
    computed: {
        modalSettings() {
            return this.$refs[this.modalKey];
        },
        contentHeight() {
            return this.widgetHeight - this.headerHeight;
        },
        author() {
            if (
                this.newsList.length > 0 &&
                this.selectedSlide < this.newsList.length
            ) {
                return this.newsList[this.selectedSlide].user;
            }
            return '';
        },
    },
    methods: {
        resize(height, width) {
            this.widgetHeight = height;
            this.widgetWidth = width;
        },
        showSettings() {
            this.modalSettings.show();
        },
        onSettingsSave(dto) {},
        removeWidget() {
            this.$emit('on-remove-widget', this.id);
        },
        refresh() {
            this.loading = true;

            var createOrUpdateInternalTableAction = {
                Action: 1, //CreateOrUpdateInternalTable
                Data: JSON.stringify({
                    TableName: this.dbTableName,
                    ColumnNames: this.dbTableColumns,
                }),
            };

            PluginService.executeAction(createOrUpdateInternalTableAction).then(
                () => {
                    const now = new Date();
                    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
                    const timeNow = `${String(now.getHours()).padStart(
                        2,
                        '0'
                    )}:${String(now.getMinutes()).padStart(2, '0')}`;
                    const filters = [
                        { ColumnName: 'startDate', Value: today, Operator: '<=', FieldType: 'Date' },
                        { ColumnName: 'endDate', Value: today, Operator: '>=', FieldType: 'Date' },
                        { ColumnName: 'startTime', Value: timeNow, Operator: '<=', FieldType: 'Time' },
                        { ColumnName: 'endTime', Value: timeNow, Operator: '>=', FieldType: 'Time' },
                    ];

                    var getDataInternalTableAction = {
                        Action: 2, //GetDataInternalTable
                        Data: JSON.stringify({
                            TableName: this.dbTableName,
                            Filters: filters,
                        }),
                    };

                    PluginService.executeAction(getDataInternalTableAction).then(
                        (result) => {
                            this.loading = false;
                            const data = result.map((dto) => {
                                return new Model(dto);
                            });

                            if (data.length == 0) {
                                this.hasData = false;
                                return;
                            }
                            const news = [];

                            for (let i = 0; i < data.length; i++) {
                                const item = data[i];

                                if (this.checkDateCondition(item)) {
                                    if (item.target == '3') {
                                        //target user
                                        var currentUserId =
                                            this.$store.getters['account/user'].Id + '';
                                        if (item.targetValue.includes(currentUserId)) {
                                            news.push(item);
                                        }
                                    } else if (item.target == '2') {
                                        //target rol

                                        var currentRoles = this.$store.getters[
                                            'account/user'
                                        ].Roles.map((r) => r.Id);

                                        var target = item.targetValue.map((r) => +r);
                                        if (target.some((r) => currentRoles.includes(r))) {
                                            news.push(item);
                                        }
                                    } else news.push(item);
                                }
                            }

                            if (news.length > 0) {
                                this.newsList = news;
                                this.hasData = true;
                            }
                        }
                    );
                }
            );
        },
        checkDateCondition(item) {
            const userFormat = `${this.$store.getters['account/user'].MetricDateFormat} HH:MM`;
            const now = new Date();
            const start = Utils.date.fromUserFormat(
                `${item.startDate} ${item.startTime}`,
                userFormat
            );

            const end =
                item.endDate && item.endTime
                    ? Utils.date.fromUserFormat(
                        `${item.endDate} ${item.endTime}`,
                        userFormat
                    )
                    : null;

            if (now > start) {
                if (end) {
                    return now < end;
                }

                return true;
            }

            return false;
        },
    },
};
</script>
<style >
.newsfeed-html-content img {
  width: 100% !important;
}
</style>
